/**
 * Winlose Service
 * 
 * Service สำหรับ query Winlose collection
 */

import { queryClass } from '@/services/adapters/parse/parse-client'
import type { Winlose, WinloseQueryParams } from './winlose.types'

export class WinloseService {
  /**
   * Map raw Parse data to Winlose interface
   */
  private static mapToWinlose(raw: any): Winlose {
    // Parse winlose_obj if it's a string (JSON)
    let winlose_obj = raw.winlose_obj || {}
    if (typeof winlose_obj === 'string') {
      try {
        winlose_obj = JSON.parse(winlose_obj)
      } catch (e) {
        console.warn('[Winlose] Failed to parse winlose_obj as JSON:', e)
        winlose_obj = {}
      }
    }
    
    // Debug: log first item's winlose_obj structure
    if (raw.objectId && !raw._debugged) {
      console.log('[Winlose] ========== DEBUG START ==========')
      console.log('[Winlose] ObjectId:', raw.objectId)
      console.log('[Winlose] Raw winlose_obj type:', typeof raw.winlose_obj)
      console.log('[Winlose] Raw winlose_obj:', raw.winlose_obj)
      console.log('[Winlose] Parsed winlose_obj:', winlose_obj)
      console.log('[Winlose] winlose_obj keys:', Object.keys(winlose_obj || {}))
      
      if (winlose_obj?.data) {
        const gameKeys = Object.keys(winlose_obj.data)
        console.log('[Winlose] Game keys in data:', gameKeys.length, gameKeys)
        if (gameKeys.length > 0) {
          const firstGame = gameKeys[0]
          console.log(`[Winlose] Sample game (${firstGame}):`, winlose_obj.data[firstGame])
          // ตรวจสอบว่ามีเกมไหนที่มีค่าไม่เป็น 0
          const gamesWithData = gameKeys.filter(key => {
            const game = winlose_obj.data[key]
            return game && (game.wl !== 0 || game.vl !== 0 || game.com !== 0 || game.stake !== 0)
          })
          console.log('[Winlose] Games with non-zero data:', gamesWithData.length, gamesWithData)
        } else {
          console.warn('[Winlose] No game keys found in winlose_obj.data')
        }
      } else {
        console.warn('[Winlose] winlose_obj.data is missing or undefined')
        console.log('[Winlose] winlose_obj structure:', JSON.stringify(winlose_obj, null, 2))
      }
      
      // ตรวจสอบ sum fields
      console.log('[Winlose] sum_wl:', raw.sum_wl)
      console.log('[Winlose] sum_vl:', raw.sum_vl)
      console.log('[Winlose] sum_com:', raw.sum_com)
      console.log('[Winlose] sum_stake:', raw.sum_stake)
      console.log('[Winlose] balance:', raw.balance)
      console.log('[Winlose] ========== DEBUG END ==========')
      raw._debugged = true // Prevent duplicate logs
    }
    
    return {
      id: raw.objectId,
      objectId: raw.objectId,
      winlose_obj: winlose_obj,
      start_date: raw.start_date || '',
      end_date: raw.end_date || '',
      update_time: raw.update_time || raw.updatedAt || '',
      member_id_str: raw.member_id_str || '',
      member_id: raw.member_id ? {
        objectId: raw.member_id.objectId,
        agent_username: raw.member_id.agent_username,
        firstname: raw.member_id.firstname,
        lastname: raw.member_id.lastname,
        username: raw.member_id.username,
      } : undefined,
      balance: raw.balance || 0,
      sum_wl: raw.sum_wl || 0,
      sum_vl: raw.sum_vl || 0,
      sum_com: raw.sum_com || 0,
      sum_stake: raw.sum_stake || 0,
      used_for_withdraw: raw.used_for_withdraw ?? 0,
      used_for_retrun_lose: raw.used_for_retrun_lose ?? 0,
      no_per_day: raw.no_per_day || 1,
      createdAt: raw.createdAt || new Date().toISOString(),
      updatedAt: raw.updatedAt || new Date().toISOString(),
    }
  }

  /**
   * Find Winlose records with pagination
   */
  static async findWinlose(
    sessionToken: string,
    params: WinloseQueryParams = {}
  ): Promise<{ items: Winlose[]; total: number }> {
    const where: Record<string, any> = {}

    // Filter by member_id_str
    if (params.member_id) {
      where.member_id_str = params.member_id
    }

    // Filter by start_date
    if (params.start_date) {
      where.start_date = params.start_date
    }

    // Filter by date range (start_date is string DD-MM-YYYY)
    // ใช้ containedIn เพื่อ query วันที่ทั้งหมดในช่วงที่เลือก
    // ตาม Angular: query.containedIn('start_date', datesArray)
    if (params.date_range) {
      if (params.date_range.start && params.date_range.end) {
        // สร้าง array ของวันที่ทั้งหมดในช่วง (DD-MM-YYYY format)
        const datesArray: string[] = []
        
        // Parse DD-MM-YYYY to Date object
        const parseDate = (dateStr: string): Date => {
          const parts = dateStr.split('-')
          if (parts.length !== 3) {
            throw new Error(`Invalid date format: ${dateStr}. Expected DD-MM-YYYY`)
          }
          const [day, month, year] = parts.map(Number)
          if (isNaN(day) || isNaN(month) || isNaN(year)) {
            throw new Error(`Invalid date values: ${dateStr}`)
          }
          return new Date(year, month - 1, day)
        }
        
        try {
          let startDate = parseDate(params.date_range.start)
          let endDate = parseDate(params.date_range.end)
          
          // ตรวจสอบว่า start <= end
          if (startDate > endDate) {
            console.warn('[Winlose] Start date is after end date, swapping...')
            const temp = startDate
            startDate = endDate
            endDate = temp
          }
          
          const current = new Date(startDate)
          while (current <= endDate) {
            const day = String(current.getDate()).padStart(2, '0')
            const month = String(current.getMonth() + 1).padStart(2, '0')
            const year = current.getFullYear()
            datesArray.push(`${day}-${month}-${year}`)
            current.setDate(current.getDate() + 1)
          }
          
          // ใช้ $in สำหรับ containedIn query (Parse REST API format)
          if (datesArray.length > 0) {
            where.start_date = { $in: datesArray }
            console.log(`[Winlose] Date range: ${datesArray.length} days (${datesArray[0]} to ${datesArray[datesArray.length - 1]})`)
          } else {
            console.warn('[Winlose] No dates in range, using empty array')
            where.start_date = { $in: [] } // จะไม่ return ข้อมูล
          }
        } catch (error) {
          console.error('[Winlose] Error parsing date range:', error)
          // Fallback: ใช้ start_date เท่านั้น
          where.start_date = params.date_range.start
        }
      } else if (params.date_range.start) {
        where.start_date = params.date_range.start
      }
    }

    // Filter by used_for_withdraw
    if (params.used_for_withdraw !== undefined) {
      where.used_for_withdraw = params.used_for_withdraw
    }

    // Filter by used_for_retrun_lose
    if (params.used_for_retrun_lose !== undefined) {
      where.used_for_retrun_lose = params.used_for_retrun_lose
    }

    // Search by member username (ต้อง include member_id ก่อน)
    if (params.query) {
      // Note: Parse REST API ไม่รองรับ $regex ใน nested field
      // ต้อง query แยกหรือใช้ Cloud Function
      // ตอนนี้จะ filter ฝั่ง client
    }

    const queryOptions: any = {
      where,
      limit: params.limit || 50,
      skip: params.page ? (params.page - 1) * (params.limit || 50) : 0,
      order: params.sort 
        ? `${params.sort.direction === 'asc' ? '' : '-'}${params.sort.field}`
        : '-createdAt',
      include: ['member_id'],
      // ⚠️ ไม่ใช้ keys parameter เพื่อให้ดึงข้อมูลทั้งหมด (รวม nested winlose_obj.data)
      // keys: [
      //   'objectId', 'winlose_obj', 'start_date', 'end_date', 'update_time',
      //   'member_id_str', 'member_id', 'balance',
      //   'sum_wl', 'sum_vl', 'sum_com', 'sum_stake',
      //   'used_for_withdraw', 'used_for_retrun_lose', 'no_per_day',
      //   'createdAt', 'updatedAt'
      // ],
    }

    const result = await queryClass('WinLose', queryOptions, sessionToken)
    
    // Debug: log raw response for first item
    if (result.results && result.results.length > 0) {
      const firstRaw = result.results[0]
      console.log('[Winlose] Raw response from Parse Server:')
      console.log('[Winlose] - objectId:', firstRaw.objectId)
      console.log('[Winlose] - winlose_obj type:', typeof firstRaw.winlose_obj)
      console.log('[Winlose] - winlose_obj:', firstRaw.winlose_obj)
      console.log('[Winlose] - sum_wl:', firstRaw.sum_wl)
      console.log('[Winlose] - sum_vl:', firstRaw.sum_vl)
    }
    
    let items = (result.results || []).map(this.mapToWinlose)

    // Filter by query (member username) ฝั่ง client
    if (params.query && items.length > 0) {
      const queryLower = params.query.toLowerCase()
      items = items.filter(item => {
        const username = item.member_id?.agent_username || item.member_id?.username || ''
        const firstname = item.member_id?.firstname || ''
        const lastname = item.member_id?.lastname || ''
        return (
          username.toLowerCase().includes(queryLower) ||
          firstname.toLowerCase().includes(queryLower) ||
          lastname.toLowerCase().includes(queryLower) ||
          item.member_id_str.toLowerCase().includes(queryLower)
        )
      })
    }

    return {
      items,
      total: result.count || items.length,
    }
  }

  /**
   * Get Winlose by ID
   */
  static async getWinloseById(
    sessionToken: string,
    id: string
  ): Promise<Winlose | null> {
    const result = await queryClass(
      'WinLose',
      {
        where: { objectId: id },
        limit: 1,
        include: ['member_id'],
      },
      sessionToken
    )

    if (result.results && result.results.length > 0) {
      return this.mapToWinlose(result.results[0])
    }

    return null
  }

  /**
   * Get Winlose by member_id_str
   */
  static async getWinloseByMemberId(
    sessionToken: string,
    memberId: string,
    params: Omit<WinloseQueryParams, 'member_id'> = {}
  ): Promise<{ items: Winlose[]; total: number }> {
    return this.findWinlose(sessionToken, {
      ...params,
      member_id: memberId,
    })
  }
}
